import { Platform, type StyleProp, type ViewStyle } from 'react-native';
import { colors } from './colors';

export const WEB_DARK_SCROLL_CLASS = 'vuelto-dark-scrollbar';
export const WEB_TEAL_RAIL_SCROLL_CLASS = 'vuelto-teal-rail-scrollbar';

let stylesInjected = false;

/** Scrollbar oscura / teal-rail para RN Web (Chrome/Edge/Firefox). Idempotente. */
export function ensureWebDarkScrollbarStyles(): void {
  if (stylesInjected || Platform.OS !== 'web' || typeof document === 'undefined') {
    return;
  }
  stylesInjected = true;

  document.getElementById('vuelto-dark-scrollbar-styles')?.remove();
  document.getElementById('vuelto-scrollbar-styles')?.remove();

  const style = document.createElement('style');
  style.id = 'vuelto-scrollbar-styles';
  style.textContent = `
    .${WEB_DARK_SCROLL_CLASS} {
      scrollbar-width: thin;
      scrollbar-color: ${colors.controlBorder} transparent;
    }
    .${WEB_DARK_SCROLL_CLASS}::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    .${WEB_DARK_SCROLL_CLASS}::-webkit-scrollbar-track {
      background: transparent;
    }
    .${WEB_DARK_SCROLL_CLASS}::-webkit-scrollbar-thumb {
      background-color: ${colors.controlBorder};
      border-radius: 999px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    .${WEB_DARK_SCROLL_CLASS}::-webkit-scrollbar-thumb:hover {
      background-color: ${colors.textMuted};
    }

    /* Rail teal desktop — thumb claro, sin pista gris */
    .${WEB_TEAL_RAIL_SCROLL_CLASS} {
      scrollbar-width: thin;
      scrollbar-color: rgba(255,255,255,0.35) transparent;
    }
    .${WEB_TEAL_RAIL_SCROLL_CLASS}::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    .${WEB_TEAL_RAIL_SCROLL_CLASS}::-webkit-scrollbar-track {
      background: transparent;
    }
    .${WEB_TEAL_RAIL_SCROLL_CLASS}::-webkit-scrollbar-thumb {
      background-color: rgba(255,255,255,0.32);
      border-radius: 999px;
      border: 1px solid transparent;
      background-clip: padding-box;
    }
    .${WEB_TEAL_RAIL_SCROLL_CLASS}::-webkit-scrollbar-thumb:hover {
      background-color: rgba(255,255,255,0.5);
    }
  `;
  document.head.appendChild(style);
}

export function webDarkScrollProps(): { className?: string } {
  if (Platform.OS !== 'web') {
    return {};
  }
  ensureWebDarkScrollbarStyles();
  return { className: WEB_DARK_SCROLL_CLASS };
}

export function webTealRailScrollProps(): { className?: string } {
  if (Platform.OS !== 'web') {
    return {};
  }
  ensureWebDarkScrollbarStyles();
  return { className: WEB_TEAL_RAIL_SCROLL_CLASS };
}

/** Combina `webDarkScrollStyle` con estilos existentes (solo web). */
export function withWebDarkScrollStyle(style?: StyleProp<ViewStyle>): StyleProp<ViewStyle> {
  if (Platform.OS !== 'web') {
    return style;
  }
  if (style == null) {
    return webDarkScrollStyle;
  }
  return Array.isArray(style) ? [webDarkScrollStyle, ...style] : [webDarkScrollStyle, style];
}

export function withWebTealRailScrollStyle(style?: StyleProp<ViewStyle>): StyleProp<ViewStyle> {
  if (Platform.OS !== 'web') {
    return style;
  }
  if (style == null) {
    return webTealRailScrollStyle;
  }
  return Array.isArray(style) ? [webTealRailScrollStyle, ...style] : [webTealRailScrollStyle, style];
}

/** className (webkit) + style (Firefox) para ScrollView, FlatList y SectionList. */
export function webOperationalScrollBindings(style?: StyleProp<ViewStyle>) {
  return {
    ...webDarkScrollProps(),
    style: withWebDarkScrollStyle(style),
  };
}

/** Scroll del sidebar teal (desktop). */
export function webTealRailScrollBindings(style?: StyleProp<ViewStyle>) {
  return {
    ...webTealRailScrollProps(),
    style: withWebTealRailScrollStyle(style),
  };
}

export const webDarkScrollStyle: ViewStyle | undefined =
  Platform.OS === 'web'
    ? ({
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.controlBorder} transparent`,
      } as ViewStyle)
    : undefined;

export const webTealRailScrollStyle: ViewStyle | undefined =
  Platform.OS === 'web'
    ? ({
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,255,255,0.35) transparent',
      } as ViewStyle)
    : undefined;
